import type {
  EventSponsorShipType,
  EventDataType,
  eventTicketShema,
  EventHighlightType,
} from '@/schemas/event.schema'
import { BR_REGEX, DATATYPE, NBSP_REGEX, SURROUNDING_P_REGEX } from '.'
import type { createEventPayload, EventHighlight } from '@/api/eventApi'
import DOMPurify from 'dompurify'
import {
  defaultActivityHighlights,
  defaultSponsorshipPackages,
  eventHighlightSchema,
  eventSponsorshipPackageSchema,
  hasPackageData,
  type EventSponsorShipPayloadType,
  type EventTicketType,
} from '@/schemas/eventForm.schema'
import type z from 'zod'
import { defaultSubEventTicket } from '@/schemas/subEventForm.schema'
import type { SubEventData } from '@/schemas/subEvent.schema'
import {
  combineDateTime,
  normalizeDate,
  extractTimeFromDateTime,
} from '@/utils/dateUtils'
import type { URLImage } from '@/hooks/useImageUpload'

export const eventStatus = {
  draft: 'Draft',
  rejected: 'Rejected',
  active: 'Active',
  open: 'Open',
  sold_out: 'Sold Out',
  closed: 'Closed',
  suspended: 'Suspended',
}

export const eventColumnsData = [
  {
    accessorKey: 'name',
    label: 'Event',
    size: 240,
    isSortable: true,
    dataType: DATATYPE.STRING,
  },
  {
    accessorKey: 'organizerName',
    label: 'Organizer',
    size: 150,
    minSize: 150,
    maxSize: 200,
    isSortable: false,
    dataType: DATATYPE.STRING,
  },
  {
    accessorKey: 'sponsors',
    label: 'Sponsor',
    size: 180,
    minSize: 200,
    maxSize: 200,
    isSortable: false,
    dataType: DATATYPE.STRING,
  },
  {
    accessorKey: 'category',
    label: 'Category',
    size: 160,
    minSize: 160,
    maxSize: 180,
    isSortable: false,
    dataType: DATATYPE.STRING,
  },
  {
    accessorKey: 'startDateTime',
    label: 'Start Date & Time',
    isSortable: true,
    dataType: DATATYPE.DATE,
  },
  {
    accessorKey: 'endDateTime',
    label: 'End Date & Time',
    isSortable: true,
    dataType: DATATYPE.DATE,
  },
  {
    accessorKey: 'attendeesCount',
    label: 'Attendees',
    isSortable: false,
    dataType: DATATYPE.NUMBER,
  },
  {
    accessorKey: 'location',
    label: 'Location',
    size: 220,
    minSize: 220,
    isSortable: false,
    dataType: DATATYPE.STRING,
  },
  {
    accessorKey: 'status',
    label: 'Status',
    isSortable: true,
    dataType: DATATYPE.STRING,
  },
  {
    accessorKey: 'actions',
    label: '',
    isSortable: false,
    dataType: DATATYPE.NONE,
  },
]

export interface LocationOption {
  id: 'online' | 'offline' | 'hybrid'
  title: string
}

export const locationOptions: LocationOption[] = [
  {
    id: 'online',
    title: 'Online',
  },
  {
    id: 'offline',
    title: 'Offline',
  },
  {
    id: 'hybrid',
    title: 'Hybrid',
  },
]

export interface registrationTypeOption {
  id: 'free' | 'paid' | 'invite-only'
  title: string
}

export const registrationOptions: registrationTypeOption[] = [
  {
    id: 'free',
    title: 'Free',
  },
  {
    id: 'paid',
    title: 'Paid',
  },
]

export const EventTimezones = {
  // --- Universal & Greenwich Mean Time ---
  UTC: 'Etc/UTC',
  GMT: 'Etc/GMT',

  // --- Africa ---
  AFRICA_CAIRO: 'Africa/Cairo', // Egypt (EET)
  AFRICA_JOHANNESBURG: 'Africa/Johannesburg', // South Africa (SAST)
  AFRICA_LAGOS: 'Africa/Lagos', // Nigeria (WAT)
  AFRICA_NAIROBI: 'Africa/Nairobi', // Kenya (EAT)

  // --- Americas ---
  AMERICA_NEW_YORK: 'America/New_York', // Eastern Time (US & Canada)
  AMERICA_CHICAGO: 'America/Chicago', // Central Time (US & Canada)
  AMERICA_DENVER: 'America/Denver', // Mountain Time (US & Canada)
  AMERICA_LOS_ANGELES: 'America/Los_Angeles', // Pacific Time (US & Canada)
  AMERICA_SAO_PAULO: 'America/Sao_Paulo', // Brazil (BRT)
  AMERICA_TORONTO: 'America/Toronto', // Eastern Time (Canada)
  AMERICA_VANCOUVER: 'America/Vancouver', // Pacific Time (Canada)
  AMERICA_MEXICO_CITY: 'America/Mexico_City', // Central Time (Mexico)

  // --- Asia ---
  ASIA_DUBAI: 'Asia/Dubai', // United Arab Emirates (GST)
  ASIA_HONG_KONG: 'Asia/Hong_Kong', // Hong Kong (HKT)
  ASIA_KOLKATA: 'Asia/Kolkata', // India (IST)
  ASIA_SEOUL: 'Asia/Seoul', // South Korea (KST)
  ASIA_SHANGHAI: 'Asia/Shanghai', // China Time
  ASIA_SINGAPORE: 'Asia/Singapore', // Singapore (SGT)
  ASIA_TOKYO: 'Asia/Tokyo', // Japan (JST)

  // --- Australia ---
  AUSTRALIA_SYDNEY: 'Australia/Sydney', // AEDT/AEST
  AUSTRALIA_PERTH: 'Australia/Perth', // AWST
  AUSTRALIA_MELBOURNE: 'Australia/Melbourne', // AEDT/AEST

  // --- Europe ---
  EUROPE_LONDON: 'Europe/London', // United Kingdom (GMT/BST)
  EUROPE_PARIS: 'Europe/Paris', // Central European Time
  EUROPE_BERLIN: 'Europe/Berlin', // Central European Time
  EUROPE_MOSCOW: 'Europe/Moscow', // Moscow Time
  EUROPE_MADRID: 'Europe/Madrid', // Central European Time
  EUROPE_ROME: 'Europe/Rome', // Central European Time

  // --- Pacific ---
  PACIFIC_AUCKLAND: 'Pacific/Auckland', // New Zealand (NZST/NZDT)
  PACIFIC_HONOLULU: 'Pacific/Honolulu', // Hawaii (HST)
}

export const createMarkup = (htmlContent: string) => {
  const sanitized = DOMPurify.sanitize(htmlContent || '', {
    ALLOWED_TAGS: [
      'p',
      'ul',
      'ol',
      'li',
      'strong',
      'em',
      'u',
      'a',
      'span',
      'br',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
    KEEP_CONTENT: true,
  })

  const cleanContent = sanitized.replaceAll(/<p>\s*<\/p>/g, '').trim()

  return { __html: cleanContent }
}

export const isContentEmpty = (htmlContent: string) => {
  if (!htmlContent) return true

  return htmlContent === '<p></p>'
}

export const registrationStatus = {
  confirmed: 'Confirmed',
  pendingPayment: 'Pending Payment',
}

export const eventStatusActions = {
  suspend: 'suspend' as const,
  delete: 'delete' as const,
  resume: 'resume' as const,
}

export const getStatusBadgeClass = (status: string | undefined) => {
  switch (status?.toLowerCase()) {
    // Event status mappings (existing)
    case eventStatus.open.toLowerCase():
      return 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100 hover:bg-ss-positive-50'
    case eventStatus.suspended.toLowerCase():
      return 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100 hover:bg-ss-warning-50'
    case eventStatus.sold_out.toLowerCase():
      return 'bg-ss-brand-50 text-ss-brand-200 border-ss-brand-100 hover:bg-ss-brand-50'
    case eventStatus.closed.toLowerCase():
    case 'cancelled':
    case 'banned':
      return 'bg-ss-critical-100/70 text-ss-critical-200/90 border-ss-critical-200/80 hover:bg-ss-critical-100/90'
    case eventStatus.draft.toLowerCase():
      return 'bg-ss-netural-200 text-ss-netural-600 border-ss-netural-400 hover:bg-ss-netural-200/90'
    case eventStatus.active.toLowerCase():
    case 'active':
      return 'bg-ss-positive-50 text-ss-positive-200 border-ss-positive-100 hover:bg-ss-positive-50'
    case 'pending':
      return 'bg-ss-warning-50 text-ss-warning-200 border-ss-warning-100'
    case 'paused':
      return 'bg-ss-action-gray-50 text-ss-action-gray-200 border-ss-action-gray-100'
    default:
      return 'bg-ss-action-gray-50 text-ss-neutral-600 border-ss-neutral-50 hover:bg-ss-action-gray-50'
  }
}

export const transformSponsorShipPlan = (
  packagePlans: z.infer<typeof eventSponsorshipPackageSchema>[]
): EventSponsorShipPayloadType[] => {
  if (!packagePlans) {
    return []
  }
  return packagePlans
    .filter(packageplan => hasPackageData(packageplan))
    .map(packagePlan => {
      return {
        ...(packagePlan.id && !packagePlan.isNew && { id: packagePlan.id }),
        name: packagePlan.name,
        price: Number(Number(packagePlan.price).toFixed(2)),
        heroDuration: packagePlan.heroDuration
          ? Number(packagePlan.heroDuration)
          : 0,
        availablePlacements: packagePlan.availablePlacements,
        description: packagePlan.description,
        priority: Number(packagePlan.priority),
        maxSponsorshipsLimit: Number(packagePlan.maxSponsorshipsLimit),
      }
    })
}

export const eventStatusType = {
  active: 'active',
}

export const tranformEventDataForPayload = (
  eventData: EventDataType
): createEventPayload => {
  const sponsorshipPackages: EventSponsorShipPayloadType[] =
    transformSponsorShipPlan(eventData?.sponsorshipPlans)
  return {
    name: eventData?.name,
    categoryId: eventData?.category.id,
    description: eventData?.description,
    startDateTime: eventData?.startDateTime,
    endDateTime: eventData?.endDateTime,
    eventAdmins: eventData?.admins.map(admin => admin?.user?.id),
    guests: eventData?.guests?.map(guest => {
      return {
        id: guest?.id,
        name: guest?.name,
        designation: guest?.designation,
        profile: guest?.profile?.key,
      }
    }),
    images: eventData?.images,
    location: eventData?.location,
    maxAttendeesLimit: eventData?.maxAttendeesLimit,
    registrationDeadline: eventData?.registrationDeadline,
    specialInstructions: eventData?.specialInstructions,
    tickets: eventData?.tickets.map(val => {
      return {
        id: val.id,
        description: val.description,
        isSubEventTicket: val.isSubEventTicket,
        name: val.name,
        perks: val.perks,
        price: Number(val.price),
        maxAttendeesLimit: Number(val?.maxAttendeesLimit),
      }
    }),
    timeZone: eventData?.timeZone,
    type: eventData?.type,
    venueAddress: eventData?.venueAddress,
    venueCity: eventData?.venueCity,
    venueCountry: eventData?.venueCountry,
    venueLatitude: eventData?.venueLatitude,
    venueLongitude: eventData?.venueLongitude,
    venueName: eventData?.venueName,
    venueState: eventData?.venueState,
    zoomLink: eventData?.zoomLink,
    eventTags: eventData?.tags.map(tags => tags.tag.id),
    sponsorshipPackages,
    status: eventStatusType.active,
  }
}

export type EventAckOptionType = 'approve' | 'reject'
export const eventAckOptions = {
  approve: 'approve' as const,
  reject: 'reject' as const,
}

export const EVENTID_PARAM = 'eventid'

export const extractSponsorShipPlan = (
  sponsorShipPlans: EventSponsorShipType[]
) => {
  if (!sponsorShipPlans || sponsorShipPlans?.length == 0) {
    return defaultSponsorshipPackages
  }

  const newSponsorShipPlan = sponsorShipPlans.map(plan => {
    return {
      ...plan,
      heroDuration: plan.heroDuration ? plan.heroDuration?.toString() : '',
      priority: plan?.priority?.toString(),
      maxSponsorshipsLimit: plan?.maxSponsorshipsLimit?.toString(),
    }
  })
  return newSponsorShipPlan
}

export const SPONSOR_STATUS = {
  ACTIVE: 'active',
}

export const EVENT_FORM_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
}

export const transformEventTickets = (
  tickets: z.infer<typeof eventTicketShema>[]
): EventTicketType[] => {
  return tickets?.map(ticket => {
    return {
      ...ticket,
      price: ticket?.price?.toString() ?? '',
      maxAttendeesLimit: ticket?.maxAttendeesLimit?.toString(),
    }
  })
}

export const EVENT_TABS = {
  MAIN_EVENT: 'main-events',
  SUB_EVENT: 'sub-events',
}

export const EVENT_ATTENDEE_TABS = {
  EVENT_REGISTRATIONS: 'registrations',
  EVENT_ATTENDEE_BOOKINGS: 'attendees',
}

export const HEROFLIGHT_DURATION = {
  MIN: 1,
  MAX: 365,
}

export const getFreeTicket = (
  type: string,
  ticket: Pick<
    z.infer<typeof eventTicketShema>,
    'name' | 'description' | 'perks'
  >
) => {
  if (!ticket || !(type === registrationOptions[0].id)) {
    return {
      ticketName: '',
      ticketDescription: '',
      ticketPerks: '',
    }
  }

  return {
    ticketName: ticket.name,
    ticketDescription: ticket.description,
    ticketPerks: ticket.perks,
  }
}

export const getSubEventTickets = (
  type: string,
  tickets: SubEventData['tickets']
) => {
  if (
    type === registrationOptions[0].id ||
    !tickets ||
    (tickets && tickets.length <= 0)
  ) {
    return defaultSubEventTicket
  }

  return {
    id: tickets[0].id,
    name: tickets[0].name || '',
    price: tickets[0].price?.toString() || '',
    description: tickets[0].description || '',
    perks: tickets[0].perks || '',
    isNew: false,
  }
}

export const getMaxHeroDuration = (startDate?: Date, endDate?: Date) => {
  if (startDate && endDate) {
    const start = normalizeDate(startDate)
    const end = normalizeDate(endDate)
    const diffTime = end.getTime() - start.getTime()
    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
    return totalDays > 0 ? totalDays : HEROFLIGHT_DURATION.MAX
  }
  return HEROFLIGHT_DURATION.MAX
}

export const getPackageBenifits = (description: string) => {
  return (
    description
      .match(/<p>(.*?)<\/p>/g)
      ?.map(p => p.replaceAll(/<\/?p>/g, '').trim())
      .filter(Boolean) || []
  )
}

export const validateHeroDuration = (
  _index: number,
  value: string | null | undefined,
  maxHeroDuration: number
): string => {
  if (!value || value.trim() === '') return ''

  const num = Number.parseInt(value)
  if (Number.isNaN(num)) return ''

  if (num <= 0) {
    return 'Hero Flight Duration must be greater than 0'
  }

  if (num > 365) {
    return 'Hero Flight Duration must not exceed 365'
  }

  if (num > maxHeroDuration) {
    return `Hero Flight Duration must not exceed ${maxHeroDuration} days`
  }

  return ''
}

export const transformEventHighlights = async (
  formEventHighLightData: z.infer<typeof eventHighlightSchema>[],
  uploadHighlightImages: (files: File[]) => Promise<string[] | undefined>,
  isEdit: boolean
) => {
  const highlightsWithFiles = (formEventHighLightData || []).filter(
    highLight =>
      (highLight.title && highLight.shortDescription) || highLight.imageFile
  )

  const filesToUpload = highlightsWithFiles
    .filter(highLight => highLight.imageFile)
    .map(highLight => highLight.imageFile!)

  let uploadedKeys: string[] = []
  if (filesToUpload.length > 0) {
    uploadedKeys = (await uploadHighlightImages(filesToUpload)) || []
  }

  let uploadKeyIndex = 0
  const eventHighlights: EventHighlight[] = highlightsWithFiles
    .filter(highLight => highLight.title && highLight.shortDescription)
    .map(highLight => {
      let imageKey = highLight.eventImage
      console.log(highLight)

      if (highLight.imageFile && uploadedKeys.length > uploadKeyIndex) {
        imageKey = uploadedKeys[uploadKeyIndex]
        uploadKeyIndex++
      }

      return {
        ...(isEdit && highLight.id && !highLight.isNew
          ? { id: highLight.id }
          : {}),
        title: highLight.title,
        description: highLight.shortDescription,
        ...(highLight?.startDate && {
          startDateTime: combineDateTime(
            highLight.startDate,
            highLight?.startTime
          )?.toISOString(),
        }),
        ...(highLight?.endDate && {
          endDateTime: combineDateTime(
            highLight?.endDate,
            highLight?.endTime
          )?.toISOString(),
        }),
        ...(imageKey && { image: imageKey }),
      }
    })

  return eventHighlights
}

export const getEventHighLightData = async (
  apiEventHighLightData: EventHighlightType[],
  getExistingEventHighLightImages: (images: URLImage[]) => Promise<File[]>
) => {
  if (!apiEventHighLightData) {
    return defaultActivityHighlights
  }

  if (apiEventHighLightData?.length <= 0) {
    return defaultActivityHighlights
  }

  const eventHighlightPromises = apiEventHighLightData?.map(
    async (highlight: EventHighlightType) => {
      let imageFile = undefined

      if (highlight?.image && highlight?.image?.key) {
        imageFile = await getExistingEventHighLightImages([highlight?.image])
      }

      return {
        id: highlight?.id || '',
        title: highlight?.title || '',
        shortDescription: highlight?.description || '',
        eventImage: highlight?.image?.key || null,
        imageFile: imageFile ? imageFile[0] : undefined,
        startDate: highlight?.startDateTime
          ? new Date(highlight.startDateTime)
          : undefined,
        startTime: highlight?.startDateTime
          ? extractTimeFromDateTime(highlight.startDateTime)
          : undefined,
        endDate: highlight?.endDateTime
          ? new Date(highlight.endDateTime)
          : undefined,
        endTime: highlight?.endDateTime
          ? extractTimeFromDateTime(highlight.endDateTime)
          : undefined,
        isNew: false,
      }
    }
  )

  const eventHighLights = await Promise.all(eventHighlightPromises)

  return eventHighLights
}

export const eventHighLightImageProperties = {
  acceptedFileTypes: ['image/png', 'image/jpeg', 'image/svg+xml'],
  maxFileSize: 2 * 1024 * 1024,
  hint: 'JPEG, PNG, SVG (max. 2MB)',
}

export const cleanSpecialInstructions = (value?: string) => {
  if (!value) return ''

  const trimmed = value.trim()

  // Remove surrounding <p>...</p>
  const withoutP = trimmed.replaceAll(SURROUNDING_P_REGEX, '').trim()

  // Normalize known empty values
  const normalized = withoutP
    .replaceAll(NBSP_REGEX, '')
    .replaceAll(BR_REGEX, '')
    .trim()

  return normalized === '' ? '' : value
}
